/**
 * GroupSidebarItem - Individual group item in sidebar
 *
 * Shows:
 * - Group initials icon with badges
 * - Group name (when expanded)
 * - Last message preview (when expanded)
 */

import React from 'react';
import { TouchableOpacity, Text, View, StyleSheet } from 'react-native';
import { Colors } from '@/constants/colors';
import { GroupInitialsIcon } from './GroupInitialsIcon';
import type { Group } from '@/services/groups/groupService';

interface GroupSidebarItemProps {
  group: Group;
  isActive: boolean;
  isExpanded: boolean;
  onPress: () => void;
}

export const GroupSidebarItem: React.FC<GroupSidebarItemProps> = ({
  group,
  isActive,
  isExpanded,
  onPress,
}) => {
  const hasUnread = (group.unread_count ?? 0) > 0;

  const handlePress = () => {
    console.log('[GroupSidebarItem] Clicked group:', group.name, group.id);
    onPress();
  };

  return (
    <TouchableOpacity
      style={[styles.item, isActive && styles.itemActive]}
      onPress={handlePress}
      activeOpacity={0.7}
      // @ts-ignore - Web-only property for better click handling
      accessibilityRole="button"
    >
      <GroupInitialsIcon
        name={group.name}
        unreadCount={group.unread_count}
        hasMention={false} // TODO: Add has_mention field to Group interface
        isActive={isActive}
        size={isExpanded ? 40 : 32}
      />

      {isExpanded && (
        <View style={styles.textContainer}>
          <Text style={[styles.groupName, hasUnread && styles.groupNameBold]} numberOfLines={1}>
            {group.name}
          </Text>
          {group.last_message && (
            <Text style={styles.lastMessage} numberOfLines={1}>
              {group.last_message.content}
            </Text>
          )}
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    // @ts-ignore - Web-only property
    transition: 'background-color 150ms ease',
    // @ts-ignore - Web-only property
    cursor: 'pointer',
  },
  itemActive: {
    backgroundColor: Colors.background.tertiary,
  },
  textContainer: {
    flex: 1,
    marginLeft: 12,
    // @ts-ignore - Web-only property
    animation: 'fadeIn 150ms ease-in 50ms both',
  },
  groupName: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  groupNameBold: {
    fontWeight: '700',
  },
  lastMessage: {
    fontSize: 12,
    color: Colors.text.tertiary,
  },
});
